import { useEffect, useMemo, useState } from 'react';
import {
  FormProvider,
  type FieldPath,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  Box,
  Button,
  Link,
  Paper,
  styled,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';

import {
  createReservationFormSchema,
  ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';

import type { LevelOfCareContext } from './machines/levelOfCare.machine';
import { useReservationFormMachines } from './ReservationFormMachinesContext';
import { useFieldActorSnapshot } from './hooks/useFieldActorSnapshot';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';

import { TripLegType } from '@/data/models/reservation.model';
import { Passenger } from '@/data/models/passenger.model';

import { selectPassengerClientData } from '@/data/client.slice';
import { getReservationNarrativeAsync } from '@/data/reservation.slice';
import { selectCurrentPassenger } from '@/data/passenger.slice';

import reservationUtils from '@/services/reservationUtils';
import { mergeAggregatedWithLiveForm } from './utils/getAggregatedFormValues';

import PassengerBenefits from '../home/passengers/PassengerBenefits';
import { TripMachineCountSync } from './fields/TripMachineCountSync';
import TabPanel from '../TabPanel';
import { ReservationSummaryMap } from './ReservationSummaryMap';
import {
  ReservationSummaryDetails,
  ReservationSummaryDetailsProps,
} from './ReservationSummaryDetails';
import { usePortalContext } from '@/contexts/PortalProvider';
import { canSaveAsDraftFromFormValues } from './machines';
import { useReservationFieldEditValidation } from './hooks/useReservationFieldEditValidation';
import { ReservationSummaryNarrative } from './ReservationSummaryNarrative';

const PREFIX = 'ReservationSummary';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  benefits: `${PREFIX}-benefits`,
  phoneNumberFields: `${PREFIX}-phoneNumberFields`,
  subctrl: `${PREFIX}-subctrl`,
  names: `${PREFIX}-names`,
  name: `${PREFIX}-name`,
  attributes: `${PREFIX}-attributes`,
  previous: `${PREFIX}-previous`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-around',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
  },
  [`& .${classes.benefits}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 550,
  },
  [`& .${classes.phoneNumberFields}`]: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: 188,
    },
  },
  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(2),
  },
  [`& .${classes.names}`]: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.name}`]: {
    flexGrow: 1,
  },
  [`& .${classes.attributes}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [`& .${classes.previous}`]: {
    display: 'flex',
    alignItems: 'end',
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    letterSpacing: 1.5,
    lineHeight: '157%',
    marginLeft: theme.spacing(-1),
  },
}));

interface ReservationSummaryProps {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onPrevious: (evt: any) => void;
}

const ReservationSummary = ({ form, onPrevious }: ReservationSummaryProps) => {
  const { t } = useTranslation('common');
  const { isPortal, isMemberPortal } = usePortalContext();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { mainActor, mainSnapshot, fieldRefs } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const isNonAmbulatoryByIndex = (levelOfCareSnap.context as LevelOfCareContext)
    .isNonAmbulatoryByIndex;
  const reservationFormSchema = useMemo(
    () => createReservationFormSchema(null, t, isNonAmbulatoryByIndex),
    [t, isNonAmbulatoryByIndex],
  );
  const passenger = useAppSelector(selectCurrentPassenger);

  const tripCount = form.getValues('trips').length;

  const tripLegs = Array(tripCount)
    .fill(0)
    .map((_, step) => {
      const tripField = (field: string) =>
        form.watch(`trips.${step}.${field}` as FieldPath<ReservationFormInput>);

      const pickupAddressStructRaw = tripField('pickupAddressStruct') as string;
      const dropoffAddressStructRaw = tripField(
        'dropoffAddressStruct',
      ) as string;

      const isWillCall = tripField('isWillCall') as boolean;
      const appointmentDateTime = tripField('appointmentDateTime');
      const appointmentDateOnly = tripField('appointmentDate') as string;
      const appointmentDateIso = isWillCall
        ? appointmentDateOnly || ''
        : appointmentDateTime instanceof DateTime && appointmentDateTime.isValid
          ? (appointmentDateTime.toISO() ?? '')
          : '';

      const roundTripWillCallFlag = tripField('roundTripWillCall') as boolean;
      const roundTripReturnDt = tripField('roundTripReturnDateTime');

      return {
        reasonForTrip: tripField('reasonForTrip') as string,
        pickupAddress: JSON.parse(pickupAddressStructRaw),
        dropoffAddress: JSON.parse(dropoffAddressStructRaw),
        pickupAddressStruct: pickupAddressStructRaw ?? '',
        dropoffAddressStruct: dropoffAddressStructRaw ?? '',
        pickupTimeZone: tripField('pickupTimeZone') as string | undefined,
        roundTripWillCall: roundTripWillCallFlag,
        roundTripReturnDateTime: roundTripReturnDt,
        isWillCall,
        pickupDate: tripField('pickupDate') as string,
        pickupDateTime: tripField('pickupDateTime'),
        tripLegType: tripField('tripLegType') as TripLegType,
        appointmentDate: appointmentDateIso,
        tripType: form.watch('tripType'),
        pickupPhone: tripField('pickupPhone') as string,
        dropoffPhone: tripField('dropoffPhone') as string,
        pickupNotes: tripField('pickupNotes') as string,
        dropoffNotes: tripField('dropoffNotes') as string,
      };
    });

  useEffect(() => {
    const { clientBC, passengerUUID, passengerPhone } = mainSnapshot.context;
    if (!mergedValues.trips.length || !clientBC || !passengerUUID) {
      return;
    }
    const { reservation, standingOrder } =
      reservationUtils.getCreateReservationPayloadFromReservationForm(
        clientBC,
        passengerUUID,
        mergedValues,
        passengerPhone,
      );

    dispatch(getReservationNarrativeAsync(reservation, standingOrder));
  }, []);

  const passengerClientData = useAppSelector(selectPassengerClientData);

  const watchedTrips = form.watch('trips');
  const watchedForm = form.watch();

  const mergedValues = useMemo(() => {
    return mergeAggregatedWithLiveForm(mainSnapshot.context, form.getValues());
  }, [mainSnapshot.context, watchedTrips, watchedForm]);

  const canSaveAsDraft = useMemo(
    () => canSaveAsDraftFromFormValues(mergedValues),
    [mergedValues],
  );

  useReservationFieldEditValidation(form);

  const handleValid = () => {
    const snap = mainActor.getSnapshot();
    const mergedValues = mergeAggregatedWithLiveForm(
      snap.context,
      form.getValues(),
    );
    mainActor.send({ type: 'SUBMIT', formValues: mergedValues });
  };

  const handleSaveAsDraft = () => {
    const snap = mainActor.getSnapshot();
    const mergedValues = mergeAggregatedWithLiveForm(
      snap.context,
      form.getValues(),
    );
    mainActor.send({ type: 'SAVE_AS_DRAFT', formValues: mergedValues });
  };

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const isSubmitting =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');

  return (
    <FormProvider {...form}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <TripMachineCountSync />
        <StyledBox
          className={classes.container}
          component="form"
          onSubmit={form.handleSubmit(handleValid)}>
          <Paper elevation={2} className={classes.form}>
            <Link
              color="primary"
              className={classes.previous}
              href="#"
              onClick={(evt) => onPrevious(evt)}
              underline="hover">
              <ChevronLeftOutlinedIcon />
              {t('common.back')}
            </Link>
            <Typography className={classes.title}>
              {t('reservation.summary')}
            </Typography>
            {!isPortal && (
              <>
                <Tabs
                  indicatorColor="primary"
                  textColor="primary"
                  value={tabIndex}
                  onChange={handleTabChange}
                  variant="standard">
                  <Tab
                    label={t('reservation.narrative')}
                    data-testid="tab-one"
                  />
                  <Tab label={t('reservation.details')} data-testid="tab-two" />
                </Tabs>
                <TabPanel value={tabIndex} index={0}>
                  <ReservationSummaryNarrative />
                </TabPanel>
                <TabPanel value={tabIndex} index={1}>
                  <ReservationSummaryDetails
                    passenger={passenger as Passenger}
                    tripLegs={
                      tripLegs as unknown as ReservationSummaryDetailsProps[]
                    }
                    form={form}
                  />
                </TabPanel>
              </>
            )}
            {isPortal && (
              <ReservationSummaryDetails
                passenger={passenger as Passenger}
                tripLegs={
                  tripLegs as unknown as ReservationSummaryDetailsProps[]
                }
                form={form}
              />
            )}
            <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}>
                {t('reservation.submit')}
              </Button>
              {!isPortal && (
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting || !canSaveAsDraft}
                  onClick={handleSaveAsDraft}>
                  {t('reservation.saveAsDraft')}
                </Button>
              )}
            </Box>
          </Paper>
          <Box className={classes.benefits}>
            {!isMemberPortal && (
              <PassengerBenefits
                appointmentDateTime={
                  form.getValues('trips.0.appointmentDateTime') ?? undefined
                }
                servicePlans={passengerClientData?.servicePlans || []}
              />
            )}
            <ReservationSummaryMap form={form} />
          </Box>
        </StyledBox>
      </LocalizationProvider>
    </FormProvider>
  );
};

export { ReservationSummary, type ReservationSummaryProps };
